import {
	type PurchaseHistory,
	useCustomerOrdersQuery,
	usePcoRecommendationsQuery,
} from '@pco/api/generated/graphql';
import { Dialog } from '@pco/atoms/Dialog';
import { Text } from '@pco/atoms/Text';
import { Typography } from '@pco/atoms/Typography';
import { DRAWER_KEYS } from '@pco/constants/drawers';
import { CTAButton } from '@pco/molecules/CTAButton';
import { DrawerWithState } from '@pco/molecules/DrawerWithStore';
import { useDrawerStore } from '@pco/molecules/DrawerWithStore/useDrawerStore';
import { LoadingScreen } from '@pco/molecules/LoadingScreen';
import { LoginOrCreate } from '@pco/molecules/LoginOrCreate';
import { useCartStore } from '@pco/stores/cart/cart.store';
import { priceAmountPrint } from '@pco/utils/cart';
import { getViewHistory } from '@pco/utils/previouslyViewed';
import { useIsLoggedIn } from '@pco/utils/useIsUserLogged';
import { useEffect, useMemo, useState } from 'react';
import { CartDrawerContent } from './CartDrawerContent';
import type { CartDrawerProps } from './index.types';

export const CartDrawer: React.FC<CartDrawerProps> = ({
	cardProps,
	listProps,
	errorDialogTitel,
	errorDialogText,
}) => {
	const drawerId = DRAWER_KEYS.ADD_TO_CART;
	const { isOpen, openDrawer, closeDrawer } = useDrawerStore(drawerId);
	const [showProduct, setShowProduct] = useState<boolean>(false);

	const {
		cartPrices,
		justAddedToCart,
		clearJustAddedToCart,
		justAddedLoading,
		showAddToCartError,
		addToCartErrorMessage,
		setShowAddToCartError,
	} = useCartStore();
	const { mainCTA, secondaryCTA, ...cardRest } = cardProps;
	const { actionCTA, ...listRest } = listProps;
	if (mainCTA) {
		const href = mainCTA.href ?? '#';
		mainCTA.onClick = () => {
			window.location.href = href;
		};
	}
	if (secondaryCTA) {
		secondaryCTA.onClick = () => {
			if (secondaryCTA.href) {
				const href = secondaryCTA.href ?? '#';
				window.location.href = href;
			} else {
				closeDrawer();
			}
		};
	}
	if (actionCTA) {
		actionCTA.onClick = () => {};
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: unneeed dependency
	useEffect(() => {
		if (!isOpen && justAddedToCart.product && !showProduct) {
			openDrawer();
		} else if (!isOpen && showProduct) {
			clearJustAddedToCart();
			setShowProduct(false);
		} else if (isOpen) {
			setShowProduct(true);
		}
	}, [JSON.stringify(justAddedToCart.product), isOpen]);

	const isLoggedIn = useIsLoggedIn();
	const { cartItems } = useCartStore();
	const { data: customerOrderData, isFetching: isFetchingCustomerOrders } =
		useCustomerOrdersQuery(
			{
				pageSize: 5,
				currentPage: 1,
			},
			{
				enabled: isLoggedIn,
			},
		);
	//biome-ignore lint/correctness/useExhaustiveDependencies: Should just be the initial cart skus on mount
	const cartSkus = useMemo(
		() =>
			cartItems?.reduce((acc: string[], val) => {
				if (!val?.product?.sku) return acc;
				return acc.concat(val.product.sku);
			}, []),
		[],
	);
	const userPurchaseHistory =
		customerOrderData?.customer?.orders?.items?.reduce(
			(acc: PurchaseHistory[], order) => {
				if (
					order?.items?.length === 0 ||
					!order?.order_date ||
					order?.items?.some((item) => !item?.product_sku)
				) {
					return acc;
				}
				return acc.concat({
					items: order?.items?.map((item) => item?.product_sku || '') || [],
					date: new Date(order?.order_date).toISOString(),
				});
			},
			[],
		);
	const userViewHistory = getViewHistory();
	const queryVariables = {
		pageType: 'Product' as const,
		cartSkus: cartSkus || [],
		userViewHistory: userViewHistory || [],
		userPurchaseHistory: userPurchaseHistory || [],
		currentSku: justAddedToCart?.product?.sku,
	};

	const { data: recommendations, isFetching: isFetchingRecommendations } =
		usePcoRecommendationsQuery(queryVariables, {
			gcTime: 0,
			staleTime: 0,
			enabled:
				!isFetchingCustomerOrders &&
				!justAddedLoading &&
				!!justAddedToCart?.product?.sku,
		});

	if (!justAddedToCart.product) {
		return null;
	}

	if (showAddToCartError) {
		return (
			<Dialog
				title={errorDialogTitel}
				open={showAddToCartError}
				error={addToCartErrorMessage}
				onOpenChange={(isOpen) => {
					setShowAddToCartError({ isCartError: isOpen });
					!isOpen && clearJustAddedToCart();
				}}
			>
				<Dialog.Content style={{ maxWidth: '640px' }}>
					<Dialog.Header style={{ paddingBottom: 0 }}>
						<Dialog.Title>
							<Typography _size="h3" $weight="semibold" $letterSpacing="narrow">
								{errorDialogTitel}
							</Typography>
						</Dialog.Title>
					</Dialog.Header>
					<Dialog.Body style={{ maxWidth: '552px', paddingTop: '12px' }}>
						<Typography _size="body16">{errorDialogText}</Typography>
					</Dialog.Body>
					<Dialog.Footer style={{ justifyContent: 'flex-end' }}>
						<CTAButton
							_kind="brand"
							onClick={() => {
								setShowAddToCartError({ isCartError: false });
								clearJustAddedToCart();
							}}
							label="Close"
						>
							Close
						</CTAButton>
					</Dialog.Footer>
				</Dialog.Content>
			</Dialog>
		);
	}

	const cartPrice =
		cartPrices?.suptotal &&
		priceAmountPrint({
			label: '',
			amount: cartPrices.suptotal.amount,
		});

	const CartDescription = (
		<Typography _size="body16">
			{cartPrice && (
				<Text bindings={{ subtotal: cartPrice }}>
					{cardProps.subTotalLabel}
				</Text>
			)}
		</Typography>
	);

	/**
	 * Show loading screen if cart or recommendations are still loading
	 */
	const shouldHideContent = justAddedLoading || isFetchingRecommendations;

	const addedToCartTitle = justAddedToCart.isAdded
		? cardRest.cardTitle
		: cardRest.cardUpdateTitle;
	const title = shouldHideContent ? undefined : addedToCartTitle;
	const description = shouldHideContent ? undefined : CartDescription;
	const headerStartContent = shouldHideContent ? undefined : (
		<LoginOrCreate label={cardRest.loginOrCreateAccount} />
	);
	const accessibilityLabel = shouldHideContent
		? 'Loading'
		: `${justAddedToCart.product?.name} ${title}`;

	return (
		<DrawerWithState
			drawerId={drawerId}
			direction="right"
			title={title}
			description={description}
			accessibilityLabel={accessibilityLabel}
			headerStartContent={headerStartContent}
		>
			{shouldHideContent ? (
				<LoadingScreen
					style={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
					}}
				/>
			) : (
				<CartDrawerContent
					recommendations={recommendations?.pcoRecommendations}
					cardRest={cardRest}
					listRest={listRest}
					mainCTA={mainCTA}
					secondaryCTA={secondaryCTA}
					actionCTA={actionCTA}
					product={justAddedToCart.product}
					quantity={justAddedToCart.quantity}
					subTotal={{
						value: `${cartPrices?.suptotal?.amount.value}`,
						currency: cartPrices?.suptotal?.amount.currency,
					}}
				/>
			)}
		</DrawerWithState>
	);
};

export default CartDrawer;
